'use client';

import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

type Tab = 'owners' | 'tenants';

const testimonials = {
  owners: [
    {
      id: 1,
      name: 'Ramesh Sharma',
      role: 'Property Owner',
      location: 'New Baneshwor',
      rating: 5,
      text: 'Sample testimonial — replace with verified customer feedback. Empire Venture Nepal has been managing my apartment building for several months. The rent collection process is organized and I receive regular updates about my property.',
    },
    {
      id: 2,
      name: 'Sushila Maharjan',
      role: 'Property Owner',
      location: 'Lazimpat',
      rating: 5,
      text: 'Sample testimonial — replace with verified customer feedback. Finding a reliable property management company in Kathmandu was a challenge. Empire Venture Nepal handles tenant concerns promptly and keeps detailed records.',
    },
    {
      id: 3,
      name: 'Deepak Thapa',
      role: 'Commercial Property Owner',
      location: 'Putalisadak',
      rating: 4,
      text: 'Sample testimonial — replace with verified customer feedback. My commercial space was vacant for a long time before Empire Venture Nepal found a suitable tenant. The screening process gave me confidence.',
    },
  ],
  tenants: [
    {
      id: 4,
      name: 'Priya Shrestha',
      role: 'Tenant',
      location: 'Hattisar',
      rating: 5,
      text: 'Sample testimonial — replace with verified customer feedback. The apartment I rent through Empire Venture Nepal is well-maintained. Maintenance requests are handled quickly and the team is always responsive.',
    },
    {
      id: 5,
      name: 'Anil Karmacharya',
      role: 'Tenant',
      location: 'Naxal',
      rating: 5,
      text: 'Sample testimonial — replace with verified customer feedback. Moving into a professionally managed property made a real difference. Everything from the lease to monthly payments is handled smoothly.',
    },
    {
      id: 6,
      name: 'Sunita Tamang',
      role: 'Tenant',
      location: 'Koteswor',
      rating: 4,
      text: 'Sample testimonial — replace with verified customer feedback. I appreciate the clear communication from the Empire Venture Nepal team. Any issues with the property are addressed without unnecessary delays.',
    },
  ],
};

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <Icon
          key={i}
          name="StarIcon"
          size={14}
          variant={i < rating ? 'solid' : 'outline'}
          className={i < rating ? 'text-accent' : 'text-border'}
        />
      ))}
    </div>
  );
}

export default function TestimonialsSection() {
  const [activeTab, setActiveTab] = useState<Tab>('owners');
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('revealed');
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const currentTestimonials = testimonials[activeTab];

  return (
    <section className="py-16 sm:py-20 bg-muted/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div ref={sectionRef} className="reveal-up text-center mb-10">
          <div className="flex items-center justify-center gap-2.5 mb-3">
            <div className="w-6 h-px bg-accent/60" />
            <span className="text-xs font-mono tracking-widest uppercase text-muted-foreground">
              Client Feedback
            </span>
            <div className="w-6 h-px bg-accent/60" />
          </div>
          <h2 className="text-display font-bold text-foreground mb-2">
            Trusted by Property Owners{' '}
            <span className="text-gradient-green">and Tenants</span>
          </h2>
          <p className="text-muted-foreground text-sm max-w-md mx-auto">
            Note: Testimonials below are sample placeholders. Replace with verified customer feedback.
          </p>

          {/* Tabs */}
          <div className="flex items-center justify-center gap-2 mt-5">
            <button
              onClick={() => setActiveTab('owners')}
              className={`px-5 py-2 text-sm font-semibold rounded-full transition-all duration-300 ${
                activeTab === 'owners' ? 'tab-active' : 'tab-inactive'
              }`}
            >
              Property Owners
            </button>
            <button
              onClick={() => setActiveTab('tenants')}
              className={`px-5 py-2 text-sm font-semibold rounded-full transition-all duration-300 ${
                activeTab === 'tenants' ? 'tab-active' : 'tab-inactive'
              }`}
            >
              Tenants
            </button>
          </div>
        </div>

        {/* Testimonial Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {currentTestimonials.map((testimonial, index) => (
            <TestimonialCard key={testimonial.id} testimonial={testimonial} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({
  testimonial,
  index,
}: {
  testimonial: { id: number; name: string; role: string; location: string; rating: number; text: string };
  index: number;
}) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => el.classList.add('revealed'), index * 100);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [index]);

  return (
    <div
      ref={cardRef}
      className="reveal-up bg-card border border-border rounded-xl p-5 shadow-card hover-lift flex flex-col gap-4"
    >
      <div className="flex items-start justify-between">
        <StarRating rating={testimonial.rating} />
        <Icon name="ChatBubbleLeftEllipsisIcon" size={18} className="text-muted-foreground/40" />
      </div>

      <p className="text-sm text-muted-foreground leading-relaxed flex-1 italic">
        &ldquo;{testimonial.text}&rdquo;
      </p>

      <div className="flex items-center gap-3 pt-3 border-t border-border">
        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-bold text-primary">
            {testimonial.name.charAt(0)}
          </span>
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">{testimonial.name}</p>
          <p className="text-xs text-muted-foreground">
            {testimonial.role} · {testimonial.location}
          </p>
        </div>
      </div>
    </div>
  );
}